import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ShotComparisonData } from '../types';
import { SpeedGauge } from './SpeedGauge';
import { AngleIndicator } from './AngleIndicator';
import { SweetSpotHeatmap } from './SweetSpotHeatmap';
import { DistanceProjection } from './DistanceProjection';
import { TrendingUp, TrendingDown, Target, Zap, RotateCw } from 'lucide-react';

interface AnalyticsDashboardProps {
  shotComparison: ShotComparisonData;
  className?: string;
}

export const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({
  shotComparison,
  className = ''
}) => {
  const { actual, optimal, optimization, actualTrajectory, optimalTrajectory } = shotComparison;

  // Helper functions
  const getQualityColor = (quality: string) => {
    switch (quality) {
      case 'excellent': return 'bg-green-100 text-green-800';
      case 'good': return 'bg-yellow-100 text-yellow-800';
      case 'fair': return 'bg-orange-100 text-orange-800';
      case 'poor': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getImprovementIcon = (delta: number) => {
    if (delta > 0) return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (delta < 0) return <TrendingDown className="h-4 w-4 text-red-600" />;
    return <Target className="h-4 w-4 text-blue-600" />;
  };

  const formatPercentage = (value: number) => `${(value * 100).toFixed(1)}%`;
  const formatNumber = (value: number, decimals: number = 1) => value.toFixed(decimals);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Shot Quality Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Shot Quality Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <Badge className={getQualityColor(actual.quality)} variant="secondary">
                {actual.quality.toUpperCase()}
              </Badge>
              <p className="text-sm text-gray-600 mt-1">Overall</p>
            </div>
            <div className="text-center">
              <Badge className={getQualityColor(actual.timing)} variant="secondary">
                {actual.timing.toUpperCase()}
              </Badge>
              <p className="text-sm text-gray-600 mt-1">Timing</p>
            </div>
            <div className="text-center">
              <Badge className={getQualityColor(actual.footwork)} variant="secondary">
                {actual.footwork.toUpperCase()}
              </Badge>
              <p className="text-sm text-gray-600 mt-1">Footwork</p>
            </div>
            <div className="text-center">
              <Badge className={getQualityColor(actual.balance)} variant="secondary">
                {actual.balance.toUpperCase()}
              </Badge>
              <p className="text-sm text-gray-600 mt-1">Balance</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Speed Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Ball Speed</CardTitle>
          </CardHeader>
          <CardContent>
            <SpeedGauge
              value={actual.ballSpeed}
              max={50}
              unit="m/s"
              color="#3b82f6"
              label="Incoming"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Bat Speed</CardTitle>
          </CardHeader>
          <CardContent>
            <SpeedGauge
              value={actual.batSpeed}
              optimal={optimal.batSpeed}
              max={50}
              unit="m/s"
              color="#10b981"
              label="Impact"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Exit Velocity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <SpeedGauge
              value={actual.exitVelocity}
              optimal={optimal.exitVelocity}
              max={70}
              unit="m/s"
              color="#f59e0b"
              label="Ball Exit"
              showImprovement={true}
              improvement={optimization.improvements.velocityDelta}
            />
          </CardContent>
        </Card>
      </div>

      {/* Angle Analysis */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Launch Angle</CardTitle>
          </CardHeader>
          <CardContent>
            <AngleIndicator
              actual={actual.launchAngle}
              optimal={optimal.launchAngle}
              min={-10}
              max={60}
              unit="°"
              label="Vertical"
              improvement={optimization.improvements.launchAngleDelta}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Horizontal Angle</CardTitle>
          </CardHeader>
          <CardContent>
            <AngleIndicator
              actual={actual.horizontalAngle}
              optimal={optimal.horizontalAngle}
              min={-90}
              max={90}
              unit="°"
              label="Direction"
              improvement={optimization.improvements.horizontalAngleDelta}
            />
          </CardContent>
        </Card>
      </div>

      {/* Distance Comparison */}
      <Card>
        <CardHeader>
          <CardTitle>Distance Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <DistanceProjection
            actualDistance={actualTrajectory.totalDistance}
            optimalDistance={optimalTrajectory.totalDistance}
            maxHeight={actualTrajectory.maxHeight}
            optimalMaxHeight={optimalTrajectory.maxHeight}
            potentialGain={optimization.potentialDistanceGain}
          />
        </CardContent>
      </Card>

      {/* Power Transfer & Sweet Spot */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Power Transfer
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span>Efficiency</span>
                <span className="font-medium">{formatPercentage(actual.powerTransfer)}</span>
              </div>
              <Progress 
                value={actual.powerTransfer * 100} 
                className="h-3"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Ball Speed</p>
                <p className="font-medium">{formatNumber(actual.ballSpeed)} m/s</p>
              </div>
              <div>
                <p className="text-gray-600">Bat Speed</p>
                <p className="font-medium">{formatNumber(actual.batSpeed)} m/s</p>
              </div>
            </div>
            
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm font-medium">Energy Transfer</p>
              <p className="text-xs text-gray-600 mt-1">
                {actual.powerTransfer > 0.8 ? 'Excellent contact - maximum energy transfer' :
                 actual.powerTransfer > 0.6 ? 'Good contact - room for improvement' :
                 'Poor contact - focus on timing and sweet spot'}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Sweet Spot Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <SweetSpotHeatmap
              impactX={actual.impactLocationX}
              impactY={actual.impactLocationY}
              sweetSpotDistance={actual.sweetSpotDistance}
              powerTransfer={actual.powerTransfer}
            />
          </CardContent>
        </Card>
      </div>

      {/* Spin & Flight Characteristics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RotateCw className="h-4 w-4" />
              Spin Analysis
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {formatNumber(actual.spinRate, 0)}
              </div>
              <p className="text-sm text-gray-600">RPM</p>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Actual</span>
                <span className="font-medium">{formatNumber(actual.spinRate, 0)} RPM</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Optimal</span>
                <span className="font-medium text-green-600">{formatNumber(optimal.spinRate, 0)} RPM</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Difference</span>
                <span className="font-medium text-amber-600">
                  {optimization.improvements.spinRateDelta > 0 ? '+' : ''}
                  {formatNumber(optimization.improvements.spinRateDelta, 0)} RPM
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Flight Characteristics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Max Height</p>
                <p className="font-medium">{formatNumber(actualTrajectory.maxHeight)} m</p>
              </div>
              <div>
                <p className="text-gray-600">Flight Time</p>
                <p className="font-medium">{formatNumber(actualTrajectory.flightTime)} s</p>
              </div>
              <div>
                <p className="text-gray-600">Landing Angle</p>
                <p className="font-medium">{formatNumber(actualTrajectory.landingAngle)}°</p>
              </div>
              <div>
                <p className="text-gray-600">Shot Type</p>
                <p className="font-medium capitalize">{actual.shotType}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Potential Improvements */}
      <Card>
        <CardHeader>
          <CardTitle>Optimization Potential</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-lg">
            <div className="text-center mb-4">
              <div className="text-4xl font-bold text-green-600">
                +{formatNumber(optimization.potentialDistanceGain)} m
              </div>
              <p className="text-sm text-gray-600">Potential Distance Gain</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2">
                {getImprovementIcon(optimization.improvements.launchAngleDelta)}
                <div>
                  <p className="font-medium">Launch Angle</p>
                  <p className="text-gray-600">
                    {optimization.improvements.launchAngleDelta > 0 ? '+' : ''}
                    {formatNumber(optimization.improvements.launchAngleDelta)}°
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {getImprovementIcon(optimization.improvements.velocityDelta)}
                <div>
                  <p className="font-medium">Exit Velocity</p>
                  <p className="text-gray-600">
                    {optimization.improvements.velocityDelta > 0 ? '+' : ''}
                    {formatNumber(optimization.improvements.velocityDelta)} m/s
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {getImprovementIcon(optimization.improvements.spinRateDelta)}
                <div>
                  <p className="font-medium">Spin Rate</p>
                  <p className="text-gray-600">
                    {optimization.improvements.spinRateDelta > 0 ? '+' : ''}
                    {formatNumber(optimization.improvements.spinRateDelta, 0)} RPM
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};